/* ============================================================================
   EL METAR ALIMENTA LAS CASILLAS DEL FRAT

   El formulario tiene tres grupos meteorológicos —salida, ruta, destino— con
   las mismas tres opciones cada uno:

     CAVOK              probIdx 0
     1500' - 5KM        probIdx 1
     Sin información    probIdx 3   ← la que MÁS puntúa

   Se contestaban a mano. En la práctica se elegía «Sin información», porque
   nadie transcribe un METAR antes de cada vuelo — y el FRAT salía peor de lo
   que la meteorología justificaba, o mejor si alguien marcaba CAVOK de memoria.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE ESTE MÓDULO NO HACE

   · **No inventa una escala nueva.** Los índices son los del formulario del
     operador, que está aprobado. Cambiarlo es un trámite ante la autoridad.

   · **No contesta el formulario solo.** PROPONE, y la propuesta queda marcada
     como automática. El piloto puede cambiarla, y si la cambia, su respuesta
     manda — él vio el cielo y la aplicación no.

   · **No completa la casilla de RUTA con un METAR.** Un METAR describe un
     aeródromo, no los 60 NM que hay en el medio. Proponer el de salida para la
     ruta sería afirmar algo que nadie midió. Esa casilla queda como está.

   · **No baja el riesgo sin dato.** Si el METAR no llega, no se toca nada: la
     casilla se queda en lo que el piloto haya puesto, que por defecto es la
     que más puntúa. Un dato ausente nunca mejora un FRAT.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { analizarMetar } from "./metar.js";

/** El `probIdx` de cada categoría en el formulario. Es lo que puntúa. */
export const PROB_POR_CATEGORIA = {
  CAVOK: 0,
  "1500-5KM": 1,
  "SIN-INFO": 3,
};

/**
 * La POSICIÓN de cada categoría dentro de la lista de opciones del grupo.
 *
 * No es lo mismo que el `probIdx`: el estado del formulario (`riskChoices`)
 * guarda cuál opción está elegida —0, 1 o 2— mientras que `probIdx` es cuánto
 * puntúa esa opción —0, 1 o 3—. Confundirlos escribe un 3 donde solo hay tres
 * opciones y la casilla queda sin selección visible.
 */
export const OPCION_POR_CATEGORIA = {
  CAVOK: 0,
  "1500-5KM": 1,
  "SIN-INFO": 2,
};

/** La etiqueta del formulario para cada categoría. Tiene que coincidir. */
export const ETIQUETA_POR_CATEGORIA = {
  CAVOK: "CAVOK",
  "1500-5KM": "1500' - 5KM",
  "SIN-INFO": "Sin información",
};

/** Las casillas que un METAR PUEDE completar. La ruta no está, a propósito. */
export const CASILLAS = ["meteoSalida", "meteoDestino"];

/**
 * Propone el índice de una casilla a partir de un METAR crudo.
 *
 * @returns {object|null} `null` cuando no hay nada que proponer
 */
export function proponerDeMetar(crudo, ahora = new Date()) {
  if (typeof crudo !== "string" || !crudo.trim()) return null;

  const a = analizarMetar(crudo, ahora);
  if (!a.valido) return null;

  /* Un texto que NO es un METAR no propone nada, aunque el analizador le
     encuentre cuatro letras seguidas que parecen un código OACI.

     `<html>404</html>` proponía el índice 3 —el peor, así que no era
     peligroso— pero con una explicación falsa: «del METAR de HTML…». Un
     registro de auditoría con esa línea es peor que uno con la casilla vacía.

     Un METAR de verdad SIEMPRE trae la hora de emisión. */
  if (!a.horaUTC) return null;

  const indice = OPCION_POR_CATEGORIA[a.categoria];
  if (indice === undefined) return null;

  return {
    /* La POSICIÓN de la opción, que es lo que el formulario guarda. */
    indice,
    /* Y cuánto puntúa, para poder mostrarlo y comprobarlo. */
    probIdx: PROB_POR_CATEGORIA[a.categoria],
    categoria: a.categoria,
    etiqueta: ETIQUETA_POR_CATEGORIA[a.categoria],
    motivo: a.motivo,
    avisos: a.avisos || [],
    oaci: a.oaci,
    horaUTC: a.horaUTC,
    /* Marca que esto lo propuso la aplicación, no el piloto. La pantalla lo
       muestra: una casilla completada sola tiene que verse distinta de una
       contestada a mano. */
    automatico: true,
    /* El viento del METAR sirve para el grupo de viento del FRAT, que se
       deriva del mayor entre salida y destino. */
    vientoKt: a.vientoLimiteKt,
  };
}

/**
 * Propuestas para salida y destino.
 *
 * @param {object} metares  `{ salida: "METAR...", destino: "METAR..." }`
 * @returns {object} `{ meteoSalida, meteoDestino, vientoKt, avisos }`
 */
export function proponerFrat(metares = {}, ahora = new Date()) {
  const salida = proponerDeMetar(metares?.salida, ahora);
  const destino = proponerDeMetar(metares?.destino, ahora);

  /* El grupo de viento se deriva del PEOR de los dos, igual que lo hace el
     formulario a mano. Con un solo METAR se usa ese; sin ninguno, null — que
     el FRAT ya trata como «sin dato de viento», que puntúa. */
  const vientos = [salida?.vientoKt, destino?.vientoKt].filter(Number.isFinite);
  const vientoKt = vientos.length > 0 ? Math.max(...vientos) : null;

  const avisos = [];
  if (salida?.avisos?.length) avisos.push(...salida.avisos.map((t) => `Salida: ${t}`));
  if (destino?.avisos?.length) avisos.push(...destino.avisos.map((t) => `Destino: ${t}`));

  /* La casilla de RUTA no se propone nunca, y se dice por qué: si el piloto ve
     salida y destino completos y ruta vacía, tiene que saber que es
     deliberado y no un fallo. */
  if (salida || destino) {
    avisos.push("La casilla de ruta no se completa: un METAR describe un aeródromo, "
      + "no el tramo entre los dos.");
  }

  return {
    meteoSalida: salida,
    meteoDestino: destino,
    vientoKt,
    avisos,
  };
}

/**
 * Aplica las propuestas sobre las respuestas actuales del FRAT.
 *
 * **Una respuesta manual del piloto NUNCA se pisa.** Él vio el cielo; la
 * aplicación leyó un texto. Solo se completan las casillas que siguen con la
 * respuesta automática anterior o sin contestar.
 *
 * @param {object} respuestas   las actuales, `{ clave: indice }`
 * @param {object} propuestas   lo que devolvió `proponerFrat`
 * @param {object} marcas       qué casillas fueron automáticas, `{ clave: true }`
 * @returns {{respuestas, marcas, cambiadas}}
 */
export function aplicarPropuestas(respuestas = {}, propuestas = {}, marcas = {}) {
  const nuevas = { ...respuestas };
  const nuevasMarcas = { ...marcas };
  const cambiadas = [];

  for (const casilla of CASILLAS) {
    const p = propuestas?.[casilla];
    if (!p) continue;

    const actual = respuestas?.[casilla];
    const sinContestar = actual === undefined || actual === null;
    const eraAutomatica = marcas?.[casilla] === true;

    /* Si el piloto contestó a mano, se respeta. Pisarlo sería descartar lo que
       vio con sus ojos por lo que dice un texto de hace media hora. */
    if (!sinContestar && !eraAutomatica) continue;

    if (nuevas[casilla] !== p.indice) cambiadas.push(casilla);
    nuevas[casilla] = p.indice;
    nuevasMarcas[casilla] = true;
  }

  return { respuestas: nuevas, marcas: nuevasMarcas, cambiadas };
}

/**
 * Texto para mostrar junto a una casilla completada automáticamente.
 *
 * Devuelve `null` para las contestadas a mano: ahí no hay nada que explicar.
 */
export function explicacionDe(propuesta) {
  if (!propuesta) return null;
  const donde = propuesta.oaci ? `${propuesta.oaci} ` : "";
  const hora = propuesta.horaUTC ? `${propuesta.horaUTC}Z` : "sin hora";
  return `Del METAR de ${donde}${hora}: ${propuesta.motivo}`;
}
